import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Briefcase, MapPin, Clock, ArrowRight, ChevronRight, Building2, Heart, Users, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  { icon: Heart, title: "Health Insurance", desc: "Comprehensive medical, dental, and vision coverage" },
  { icon: GraduationCap, title: "Education Support", desc: "Tuition reimbursement and CME allowances" },
  { icon: Users, title: "Work-Life Balance", desc: "Flexible scheduling and generous PTO" },
  { icon: Building2, title: "Career Growth", desc: "Clear advancement paths and mentorship" },
];

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    cover_letter: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
    setLoading(false);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/jobs/apply`, {
        ...formData,
        job_id: selectedJob.id,
      });
      toast.success("Application submitted successfully!");
      setIsApplyOpen(false);
      setFormData({ name: "", email: "", phone: "", cover_letter: "" });
      setSelectedJob(null);
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#14213D] py-20" data-testid="careers-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="inline-block px-4 py-1.5 bg-[#00B4D8]/20 text-[#00B4D8] text-sm font-medium rounded-full mb-6">
                Join Our Team
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
                Build Your Career in Healthcare
              </h1>
              <p className="text-gray-300 text-lg mb-8">
                Join a team of dedicated professionals committed to providing exceptional 
                patient care across America. Explore opportunities that match your expertise.
              </p>
              <Button
                onClick={() => document.getElementById('openings').scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#00B4D8] text-white rounded-lg px-6 py-5 hover:bg-[#0096c7]"
                data-testid="view-openings-btn"
              >
                View Open Positions
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=800"
                alt="Healthcare team"
                className="w-full h-80 object-cover rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#F1FAEE]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#00B4D8] text-sm font-medium uppercase tracking-wider">Why Join Us</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#14213D] mt-2">
              Benefits & Perks
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 card-hover"
                data-testid={`benefit-${index}`}
              >
                <div className="w-12 h-12 bg-[#00B4D8]/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-[#00B4D8]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#14213D] mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Openings */}
      <section id="openings" className="py-20 bg-white" data-testid="job-openings">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-[#00B4D8] text-sm font-medium uppercase tracking-wider">Opportunities</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#14213D] mt-2">
              Current Openings
            </h2>
          </motion.div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-32 animate-pulse" />
              ))}
            </div>
          ) : jobs.length > 0 ? (
            <div className="space-y-4">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#F1FAEE] rounded-xl p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedJob(job)}
                  data-testid={`job-${job.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-xl text-[#14213D] mb-2">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {job.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {job.type}
                        </span>
                      </div>
                    </div>
                    <Button
                      className="bg-[#14213D] text-white rounded-lg"
                      data-testid={`view-job-${job.id}`}
                    >
                      View Details
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-[#F1FAEE] rounded-xl">
              <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-xl text-[#14213D] mb-2">
                No openings at this time
              </h3>
              <p className="text-gray-500">
                Check back soon or submit your resume for future opportunities.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Job Detail Modal */}
      {selectedJob && !isApplyOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            data-testid="job-modal"
          >
            <div className="p-6 border-b">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="font-heading font-bold text-2xl text-[#14213D]">
                    {selectedJob.title}
                  </h2>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-4 h-4" />
                      {selectedJob.department}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {selectedJob.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {selectedJob.type}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="close-job-modal"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h3 className="font-semibold text-[#14213D] mb-2">Description</h3>
                <p className="text-gray-600">{selectedJob.description}</p>
              </div>

              {selectedJob.requirements?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-[#14213D] mb-2">Requirements</h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {selectedJob.requirements.map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedJob.benefits?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-[#14213D] mb-2">Benefits</h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    {selectedJob.benefits.map((benefit, i) => (
                      <li key={i}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Button
                className="w-full bg-[#00B4D8] text-white rounded-lg py-6 hover:bg-[#0096c7]"
                onClick={() => setIsApplyOpen(true)}
                data-testid="apply-now-btn"
              >
                Apply Now
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Apply Dialog */}
      <Dialog open={isApplyOpen} onOpenChange={setIsApplyOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Apply for {selectedJob?.title}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleApply} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your full name"
                className="rounded-lg"
                data-testid="apply-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="rounded-lg"
                data-testid="apply-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="rounded-lg"
                data-testid="apply-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cover_letter">Cover Letter</Label>
              <Textarea
                id="cover_letter"
                value={formData.cover_letter}
                onChange={(e) => setFormData({ ...formData, cover_letter: e.target.value })}
                placeholder="Tell us why you'd be a great fit..."
                className="rounded-lg"
                rows={4}
                data-testid="apply-cover"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#00B4D8] text-white rounded-lg py-5"
              data-testid="submit-application"
            >
              {submitting ? "Submitting..." : "Submit Application"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-20 bg-[#00B4D8]" data-testid="careers-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Don't See a Perfect Fit?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              We're always looking for talented individuals. Send us your resume and 
              we'll keep you in mind for future opportunities.
            </p>
            <Link to="/contact">
              <Button
                className="bg-white text-[#00B4D8] rounded-lg px-6 py-5 hover:bg-gray-100"
                data-testid="contact-careers"
              >
                Contact Our HR Team
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
